/**
 * Plot image export — rasterize, copy, and download utilities for PlotCard.
 *
 * Cache/export scope: one capture per user gesture (copy/download click). Nothing
 * here is a repeated LLM flow — no prompt caching concerns apply.
 *
 * Dark-mode decision (Step B0, ratified 2026-07-16): approach (b) —
 * export in the viewer's CURRENT theme (WYSIWYG) with a matching OPAQUE background.
 * We deliberately do NOT clone-stamp light-theme tokens onto the captured node:
 * `html-to-image` has no clone hook and inlines already-computed (theme-resolved)
 * styles at capture time, and Chart.js canvases are painted pixels that DOM
 * restyling cannot recolor. So the export mirrors what the viewer sees; the only
 * synthesized value is an opaque background (resolved from the node's own
 * background, else the `--card`/`--background` design tokens) so the PNG is never
 * transparent. Future editors: do not re-add clone-stamping — it is infeasible here.
 *
 * Bundle/gesture seam: this module is intentionally TINY and safe to import
 * statically — `html-to-image` is lazy-imported INSIDE `rasterizePlot` so the heavy
 * lib stays out of the main bundle while `navigator.clipboard.write` is still
 * reached synchronously within the user gesture (Safari rejects writes once the
 * click's transient activation has expired, so no `await` may precede it).
 * Error contract: functions toast on success only and THROW on failure — the
 * caller owns the error toast (and any success-state UI like the copied check).
 */

import { toast } from 'sonner';

const FALLBACK_FILENAME = 'quick-study-plot.png';

/**
 * True when a CSS color string paints an opaque pixel (alpha !== 0 and not the
 * `transparent` keyword). Used to decide whether the node already has a usable
 * background or whether we must resolve one from design tokens.
 */
function isOpaqueColor(color: string): boolean {
  if (!color) {
    return false;
  }

  const normalized = color.trim().toLowerCase();
  if (normalized === 'transparent') {
    return false;
  }

  const rgbaMatch = normalized.match(/^rgba?\(([^)]+)\)$/);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((part) => part.trim());
    if (parts.length === 4) {
      const alpha = Number.parseFloat(parts[3]);
      return Number.isFinite(alpha) && alpha !== 0;
    }
    return true;
  }

  // Any other representation (hex, hsl, named color) is treated as opaque.
  return true;
}

/**
 * Resolve an opaque background from design tokens on the document root. The tokens
 * are HSL triplets (e.g. `0 0% 100%`) that must be wrapped as `hsl(...)`. Falls
 * back to the `white` keyword when no token is available (e.g. non-browser env).
 */
function resolveTokenBackground(): string {
  const rootStyles = getComputedStyle(document.documentElement);

  const card = rootStyles.getPropertyValue('--card').trim();
  if (card) {
    return `hsl(${card})`;
  }

  const background = rootStyles.getPropertyValue('--background').trim();
  if (background) {
    return `hsl(${background})`;
  }

  return 'white';
}

/**
 * Prefer the node's own computed background when it is already opaque; otherwise
 * resolve an opaque background from design tokens so the PNG is never transparent.
 */
function resolveOpaqueBackground(node: HTMLElement): string {
  const computedBackground = getComputedStyle(node).backgroundColor;
  if (isOpaqueColor(computedBackground)) {
    return computedBackground;
  }

  return resolveTokenBackground();
}

/**
 * Capture filter: drop any element flagged with `data-capture-exclude` (e.g. the
 * export toolbar) while keeping every other node. `html-to-image` may pass
 * non-Element nodes (text nodes), which we always keep.
 */
function captureFilter(domNode: HTMLElement): boolean {
  if (!(domNode instanceof Element)) {
    return true;
  }

  return !domNode.hasAttribute('data-capture-exclude');
}

/**
 * Turn a candidate filename into a safe PNG basename: strip path separators and
 * illegal characters, collapse whitespace to dashes, lowercase, and guarantee a
 * single `.png` suffix. Falls back to a stable default when nothing usable remains.
 */
function sanitizeFilename(filename: string): string {
  if (!filename) {
    return FALLBACK_FILENAME;
  }

  const withoutExtension = filename.replace(/\.png$/i, '');
  const base = withoutExtension
    .replace(/[/\\]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-_]+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  if (!base) {
    return FALLBACK_FILENAME;
  }

  return `${base}.png`;
}

/**
 * Rasterize a plot node to an opaque PNG blob at 2x pixel density.
 *
 * Waits for fonts so Inter renders in the capture, resolves an opaque background
 * per the Step B0 WYSIWYG decision, and excludes `data-capture-exclude` nodes.
 */
export async function rasterizePlot(node: HTMLElement): Promise<Blob> {
  const { toBlob } = await import('html-to-image');

  try {
    await document.fonts.ready;
  } catch {
    // `document.fonts` is unavailable in some environments — capture anyway.
  }

  const backgroundColor = resolveOpaqueBackground(node);

  const blob = await toBlob(node, {
    backgroundColor,
    pixelRatio: 2,
    cacheBust: true,
    filter: captureFilter,
  });

  if (!blob) {
    throw new Error('Failed to rasterize plot: no image data was produced.');
  }

  return blob;
}

/**
 * Copy the plot to the clipboard as a PNG.
 *
 * Safari requires the Blob PROMISE (not a pre-awaited Blob) inside `ClipboardItem`
 * so the write stays within the user gesture — we pass `rasterizePlot(node)`
 * directly. When the Clipboard image API is unavailable, we fall back to a download.
 */
export async function copyPlotImage(node: HTMLElement): Promise<void> {
  const hasClipboardImageSupport =
    typeof ClipboardItem !== 'undefined' && Boolean(navigator.clipboard?.write);

  if (!hasClipboardImageSupport) {
    await downloadPlotImage(node, FALLBACK_FILENAME);
    return;
  }

  const clipboardItem = new ClipboardItem({ 'image/png': rasterizePlot(node) });
  await navigator.clipboard.write([clipboardItem]);
  toast.success('Image copied');
}

/**
 * Download the plot as a PNG file. Rasterizes to a blob, triggers a temporary
 * anchor download, and always revokes the object URL afterwards.
 */
export async function downloadPlotImage(node: HTMLElement, filename: string): Promise<void> {
  let objectUrl: string | null = null;

  try {
    const blob = await rasterizePlot(node);
    objectUrl = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = sanitizeFilename(filename);
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    toast.success('Image downloaded');
  } finally {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
